'use client';

import { useLocale, useTranslations } from 'next-intl';
import React, { useRef } from 'react';

import HomePageCard from '@/app/[locale]/(default-layout)/_components/HomePageCard';
import SectionTitle from '@/app/[locale]/(default-layout)/_components/SectionTitle';

import { getFormImageUrl } from '@cineamo/api/models/website-configuration/form-image-helper';

import {
    ID_CINEWORLD_RECKLINGHAUSEN,
    ID_INNENSTADT_KINOS,
    ID_MOZARTKINO,
    ID_UFA_DUESSELDORF
} from '@cineamo/api/constants/cinema-ids';

import { loadAsset } from '@cineamo/ui/src/helpers/asset-helper';
import { isLocalEnvironment } from '@cineamo/ui/src/helpers/environment-helper';
import { styleFaded } from '@cineamo/ui/src/helpers/style-helper';

import useScrollState from '@cineamo/ui/src/hooks/useScrollState';

import BackAndForthButtonOverlay from '@cineamo/ui/src/button/BackAndForthButtonOverlay';

import CenterLayout from '@cineamo/composables/src/CenterLayout';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

const RentACinemaImage = loadAsset('/images/placeholders/im-cinemaScreen.png');
const RentACinemaImageInnenstadtKinos = 'https://cdn.cineamo.com/tmp/cinema-778-innenstadtkinos/rentcinema2.jpg';
const BirthdayAtTheCinemaImage = loadAsset('/images/pictures/im-cardSharePopcorn.png');
const BirthdayAtTheCinemaImageInnenstadtKinos = 'https://cdn.cineamo.com/tmp/cinema-778-innenstadtkinos/birthday_2.jpg';
const KidsNTeenImage = loadAsset('/images/pictures/im-schoolCinema.png');
const KidsNTeenImageInnenstadtKinos = 'https://cdn.cineamo.com/tmp/cinema-778-innenstadtkinos/kidsnteens.jpg';
const NewsletterImage = loadAsset('/images/pictures/im-marketingAssistant.png');
const NewsletterImageInnenstadtKinos = 'https://cdn.cineamo.com/tmp/cinema-778-innenstadtkinos/newsletter.jpg';
const GiftCardsImage = loadAsset('/images/pictures/im-cardPopcorn.png');
const ReservationImage = loadAsset('/images/placeholders/im-cinemaScreen.png');
const RentACinemaImageCineworldRecklinghausen =
    'https://cdn.cineamo.com/cinema-card-image/2a699347-2267-474a-93c5-fd5b0ced83cf';
const RentACinemaImageUfaDueseldorf =
    'https://cdn.cineamo.com/cinema-card-image/d6556406-4436-4868-bdf3-0b1714c9a1ac.jpg';

type HomeFormLinksSectionProps = {
    /** Cinema-hall photo used as the form-card image fallback when no custom image is uploaded. */
    hallImageUrl?: string;
};

const HomeFormLinksSection: React.FC<HomeFormLinksSectionProps> = ({ hallImageUrl }) => {
    const t = useTranslations();
    const scrollContainerRef = useRef(null);
    const domainConfiguration = useDomainConfiguration();
    const locale = useLocale();
    const cinema = domainConfiguration.cinema;

    const formsConfig = cinema?._embedded?.cinemaConfiguration?.websiteConfiguration?.forms;

    // Build cards array based on available form configurations
    const formCards = [];

    const isCinemaInnenstadtkinos = cinema?.id === ID_INNENSTADT_KINOS;

    if (
        formsConfig?.rentACinema?.emailTo &&
        formsConfig.rentACinema.isActive !== false &&
        formsConfig.rentACinema.isVisible !== false
    ) {
        let titleOverride;
        let textOverride;
        let buttonTextOverride;
        let urlOverride;
        let cardBackgroundOverride;
        let cardVideoOverride;
        switch (cinema.id) {
            case ID_UFA_DUESSELDORF:
                titleOverride = locale === 'de' ? 'Unsere Kinosäle können Sie mieten' : null;
                textOverride =
                    locale === 'de'
                        ? 'Firmenfeiern, Präsentationen, Meetings und mehr: Bei uns lässt es sich filmreif feiern! Stellen Sie jetzt unverbindlich Ihre Anfrage.'
                        : null;
                cardBackgroundOverride = RentACinemaImageUfaDueseldorf;
                cardVideoOverride = 'https://youtu.be/mc3kcD6zS5s';
                break;
            case ID_CINEWORLD_RECKLINGHAUSEN:
                titleOverride = locale === 'de' ? 'Unsere Kinosäle können Sie mieten' : null;
                textOverride =
                    locale === 'de'
                        ? 'Firmenfeiern, Präsentationen, Meetings und mehr: Bei uns lässt es sich filmreif feiern! Melden Sie sich einfach unverbindlich per Email bei uns: vermietung@cineworld-recklinghausen.de'
                        : null;
                buttonTextOverride = locale === 'de' ? 'Jetzt per Email anfragen' : null;
                urlOverride = 'mailto:vermietung@cineworld-recklinghausen.de?subject=Anfrage%20Kino%20mieten';
                cardBackgroundOverride = RentACinemaImageCineworldRecklinghausen;
                cardVideoOverride = 'https://youtu.be/Yazwn5SsI6I';
                break;
            case ID_INNENSTADT_KINOS:
                cardBackgroundOverride = RentACinemaImageInnenstadtKinos;
                break;
        }
        formCards.push({
            title: formsConfig.rentACinema.cardTitle || titleOverride || t('rent-a-cinema.page-title'),
            text:
                formsConfig.rentACinema.cardText ||
                formsConfig.rentACinema.description ||
                textOverride ||
                t('rent-a-cinema.hero.subtitle'),
            buttonText: formsConfig.rentACinema.cardButtonText || buttonTextOverride || t('rent-a-cinema.hero.cta'),
            url: urlOverride || '/rent-a-cinema',
            cardBackground:
                getFormImageUrl(formsConfig.rentACinema.imageRef) ||
                cardBackgroundOverride ||
                hallImageUrl ||
                RentACinemaImage,
            cardVideo: cardVideoOverride || null
        });
    }

    if (
        formsConfig?.birthdayAtTheCinema?.emailTo &&
        formsConfig.birthdayAtTheCinema.isActive !== false &&
        formsConfig.birthdayAtTheCinema.isVisible !== false
    ) {
        formCards.push({
            title: formsConfig.birthdayAtTheCinema.cardTitle || t('birthday-at-the-cinema.page-title'),
            text:
                formsConfig.birthdayAtTheCinema.cardText ||
                formsConfig.birthdayAtTheCinema.description ||
                t('birthday-at-the-cinema.hero.subtitle'),
            buttonText:
                formsConfig.birthdayAtTheCinema.cardButtonText ||
                t('birthday-at-the-cinema.hero.cta') ||
                t('common.learn-more'),
            url: '/birthday-at-the-cinema',
            cardBackground:
                getFormImageUrl(formsConfig.birthdayAtTheCinema.imageRef) ||
                (isCinemaInnenstadtkinos ? BirthdayAtTheCinemaImageInnenstadtKinos : undefined) ||
                hallImageUrl ||
                BirthdayAtTheCinemaImage
        });
    }

    if (
        formsConfig?.kidsNTeens?.emailTo &&
        formsConfig.kidsNTeens.isActive !== false &&
        formsConfig.kidsNTeens.isVisible !== false
    ) {
        formCards.push({
            title: formsConfig.kidsNTeens.cardTitle || t('kids-n-teens.page-title'),
            text:
                formsConfig.kidsNTeens.cardText ||
                formsConfig.kidsNTeens.description ||
                t('kids-n-teens.hero.subtitle'),
            buttonText: formsConfig.kidsNTeens.cardButtonText || t('kids-n-teens.hero.cta'),
            url: '/kids-n-teens',
            cardBackground:
                getFormImageUrl(formsConfig.kidsNTeens.imageRef) ||
                (isCinemaInnenstadtkinos ? KidsNTeenImageInnenstadtKinos : undefined) ||
                hallImageUrl ||
                KidsNTeenImage
        });
    }

    if (
        formsConfig?.giftCards?.emailTo &&
        formsConfig.giftCards.isActive !== false &&
        formsConfig.giftCards.isVisible !== false
    ) {
        formCards.push({
            title: formsConfig.giftCards.cardTitle || t('buy-gift-cards.page-title'),
            text:
                formsConfig.giftCards.cardText ||
                formsConfig.giftCards.description ||
                t('buy-gift-cards.hero.subtitle'),
            buttonText: formsConfig.giftCards.cardButtonText || t('buy-gift-cards.hero.cta'),
            url: '/buy-gift-cards',
            cardBackground: getFormImageUrl(formsConfig.giftCards.imageRef) || hallImageUrl || GiftCardsImage
        });
    }

    // Reservation form currently only available for Mozartkino (CIN-14063)
    if (
        cinema?.id === ID_MOZARTKINO &&
        formsConfig?.reservation?.emailTo &&
        formsConfig.reservation.isActive !== false &&
        formsConfig.reservation.isVisible !== false
    ) {
        formCards.push({
            title: formsConfig.reservation.cardTitle || t('reservation.page-title'),
            text:
                formsConfig.reservation.cardText ||
                formsConfig.reservation.description ||
                t('reservation.hero.subtitle'),
            buttonText: formsConfig.reservation.cardButtonText || t('reservation.hero.cta'),
            url: '/reservation',
            cardBackground: getFormImageUrl(formsConfig.reservation.imageRef) || hallImageUrl || ReservationImage
        });
    }

    // Newsletter currently only for the cinema with ID 778 (Innstadtkinos) available.
    // Its form lives on /embedded/newsletter - /newsletter only serves the Cineamo newsletter.
    if (isCinemaInnenstadtkinos || isLocalEnvironment()) {
        formCards.push({
            title: t('newsletter.page-title'),
            text: t('newsletter.hero.subtitle'),
            buttonText: t('newsletter.hero.cta'),
            url: '/embedded/newsletter',
            cardBackground: isCinemaInnenstadtkinos ? NewsletterImageInnenstadtKinos : NewsletterImage
        });
    }

    // Don't render the section if no forms are configured
    if (formCards.length === 0) {
        return null;
    }

    const { styleFadedState } = useScrollState({
        scrollContainerRef: scrollContainerRef,
        initialState: {
            startReached: true,
            endReached: false
        },
        refreshTrigger: formCards
    });

    const fadeStyle = styleFaded(styleFadedState, 5);

    return (
        <section
            role="group"
            aria-label={t('home.our-services')}>
            <CenterLayout className="space-y-12 lg:space-y-16">
                <SectionTitle title={t('home.our-services')} />
                <div className="relative flex flex-row">
                    <div
                        className="relative w-full flex flex-row space-x-16 overflow-x-auto no-scrollbar"
                        style={fadeStyle}
                        ref={scrollContainerRef}>
                        {formCards.map((card, index) => (
                            <HomePageCard
                                key={`HomeFormCard#${index}`}
                                title={card.title}
                                text={card.text}
                                buttonText={card.buttonText}
                                url={card.url}
                                cardBackground={card.cardBackground}
                                video={card.cardVideo ?? undefined}
                            />
                        ))}
                    </div>
                    {formCards.length > 0 && (
                        <BackAndForthButtonOverlay
                            scrollContainerRef={scrollContainerRef}
                            renderOutside
                        />
                    )}
                </div>
            </CenterLayout>
        </section>
    );
};

export default HomeFormLinksSection;
